/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  INetworkAvailabilityEvaluationListener,
  INetworkAvailabilityService,
  INetworkResourceListener
} from "../interfaces";
import { Log } from "../logging";
import { NetworkResource, NetworkResourceFailureReason } from "../enums";
import type { INetworkResourceAvailability } from "../types";
import { NetworkResourceNode } from "./NetworkResourceNode";
import { PropagationRule } from "./PropagationRule";
import { NetworkInterfaceNode } from "./nodes/NetworkInterfaceNode";
import { InternetNode } from "./nodes/InternetNode";

/**
 * Pending hint — holds the last-received external availability hint
 * while a propagation cycle is in progress or during initialization.
 * Only one hint is retained at a time; any prior pending hint is discarded.
 */
interface PendingHint {
  resource: NetworkResource;
  isAvailable: boolean;
  reason: NetworkResourceFailureReason | null;
}

/**
 * Concrete implementation of INetworkAvailabilityService.
 *
 * The central orchestrator for the NetworkAvailabilityService. Responsible for:
 * 1. Node creation & wiring — creates one node per NetworkResource and wires
 *    the dependency graph on startup.
 * 2. External signal registration — registers hint-accepting nodes with
 *    the NetworkResourceEventDispatcher.
 * 3. Propagation coordination — receives state-change callbacks from nodes,
 *    applies propagation rules to dependent nodes, then notifies listeners
 *    once the tree settles.
 * 4. Re-evaluation window management — while any node has not yet reported,
 *    isEvaluatingNetworkResources() returns true and getAvailability() returns
 *    the last known (cached) state.
 */
class NetworkAvailabilityServiceImpl implements INetworkAvailabilityService {
  private static readonly TAG: string = "NetworkAvailabilityServiceImpl";

  private static mInstance: NetworkAvailabilityServiceImpl | null = null;

  /** Map of network resource to its corresponding node. */
  private readonly mNodes: Map<NetworkResource, NetworkResourceNode>;

  /** Per-resource listener sets. */
  private readonly mListeners: Map<
    NetworkResource,
    Set<INetworkResourceListener>
  >;

  /** End-of-cycle evaluation listeners. */
  private readonly mEvaluationListeners: Set<INetworkAvailabilityEvaluationListener>;

  /** Cached availability snapshots (returned during graph re-evaluation). */
  private readonly mCachedAvailability: Map<
    NetworkResource,
    INetworkResourceAvailability
  >;

  /** Set of nodes currently pending re-evaluation. */
  private readonly mPendingNodes: Set<NetworkResource>;

  /** Last-received external hint during an active propagation cycle or initialization. Previous hints are discarded. */
  private mPendingHint: PendingHint | null;

  /** Whether a propagation cycle is currently in progress. */
  private mIsPropagating: boolean;

  /** Nodes whose state changed as a direct consequence of propagation, queued for further propagation within the same cycle. */
  private mPropagationQueue: NetworkResourceNode[];

  /** Nodes already visited during the current propagation cycle — used to detect and break cycles. */
  private mPropagationVisited: Set<NetworkResource>;

  /**
   * True while createAndWireNodes() is still calling node.init().
   * State-change callbacks fired during this window are suppressed so that
   * propagation is not attempted before all nodes have been initialized.
   */
  private mIsInitializing: boolean;

  private constructor() {
    this.mNodes = new Map();
    this.mListeners = new Map();
    this.mEvaluationListeners = new Set();
    this.mCachedAvailability = new Map();
    this.mPendingNodes = new Set();
    this.mPendingHint = null;
    this.mIsPropagating = false;
    this.mPropagationQueue = [];
    this.mPropagationVisited = new Set();
    this.mIsInitializing = true;

    this.createAndWireNodes();

    this.mIsInitializing = false;
    this.startUp();
  }

  /**
   * Returns the singleton instance of the NetworkAvailabilityService.
   * @returns The shared NetworkAvailabilityServiceImpl instance
   */
  public static getInstance(): NetworkAvailabilityServiceImpl {
    if (
      NetworkAvailabilityServiceImpl.mInstance === null ||
      NetworkAvailabilityServiceImpl.mInstance === undefined
    ) {
      NetworkAvailabilityServiceImpl.mInstance =
        new NetworkAvailabilityServiceImpl();
    }
    return NetworkAvailabilityServiceImpl.mInstance;
  }

  // ──────────────────────────────────────────────────────────────────
  // Consumer-Facing API (INetworkAvailabilityService)
  // ──────────────────────────────────────────────────────────────────

  /**
   * @inheritdoc
   */
  public addListener(
    resource: NetworkResource,
    listener: INetworkResourceListener
  ): void {
    let listeners: Set<INetworkResourceListener> | undefined =
      this.mListeners.get(resource);
    if (listeners === undefined) {
      listeners = new Set();
      this.mListeners.set(resource, listeners);
    }
    listeners.add(listener);
  }

  /**
   * @inheritdoc
   */
  public removeListener(
    resource: NetworkResource,
    listener: INetworkResourceListener
  ): void {
    const listeners: Set<INetworkResourceListener> | undefined =
      this.mListeners.get(resource);
    if (listeners !== undefined) {
      listeners.delete(listener);
    }
  }

  /**
   * @inheritdoc
   */
  public getAvailability(
    resource: NetworkResource
  ): INetworkResourceAvailability {
    if (this.mPendingNodes.size > 0) {
      const cached: INetworkResourceAvailability | undefined =
        this.mCachedAvailability.get(resource);
      if (cached !== undefined) {
        return cached;
      }
    }

    const node: NetworkResourceNode | undefined = this.mNodes.get(resource);
    if (node !== undefined) {
      return node.getAvailability();
    }

    return NetworkResourceNode.createDefaultAvailability();
  }

  /**
   * @inheritdoc
   */
  public isEvaluatingNetworkResources(): boolean {
    return this.mPendingNodes.size > 0;
  }

  /**
   * @inheritdoc
   */
  public addNetworkAvailabilityEvaluationListener(
    listener: INetworkAvailabilityEvaluationListener
  ): void {
    this.mEvaluationListeners.add(listener);
  }

  /**
   * @inheritdoc
   */
  public removeNetworkAvailabilityEvaluationListener(
    listener: INetworkAvailabilityEvaluationListener
  ): void {
    this.mEvaluationListeners.delete(listener);
  }

  // ──────────────────────────────────────────────────────────────────
  // Node Creation & Wiring
  // ──────────────────────────────────────────────────────────────────

  /**
   * Creates all network resource nodes, wires the dependency graph,
   * registers hint-accepting nodes with the event dispatcher, and
   * initializes each node.
   */
  private createAndWireNodes(): void {
    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "createAndWireNodes() building dependency graph"
    );

    const onStateChanged = (node: NetworkResourceNode): void => {
      this.onNodeStateChanged(node);
    };

    // --- Create nodes ---
    const niNode: NetworkInterfaceNode = new NetworkInterfaceNode(
      onStateChanged
    );
    const internetNode: InternetNode = new InternetNode(onStateChanged);

    // --- Wire dependency graph (Propagation Rules) ---

    // NETWORK_INTERFACE dependencies
    niNode.setDependency(
      internetNode,
      PropagationRule.PROPAGATE, // NI down → Internet certainly down
      // PropagationRule.RE_EVALUATE // NI up → must probe Internet
      PropagationRule.PROPAGATE // TODO : In Unity 5.0, NI = Internet
    );

    // INTERNET dependencies
    internetNode.setDependency(
      niNode,
      PropagationRule.RE_EVALUATE, // Internet down → re-check NI
      PropagationRule.PROPAGATE // Internet up → NI certainly up
    );

    // --- Register in node map ---
    this.mNodes.set(NetworkResource.NETWORK_INTERFACE, niNode);
    this.mNodes.set(NetworkResource.INTERNET, internetNode);

    // --- Initialize cached availability ---
    this.mNodes.forEach(
      (node: NetworkResourceNode, resource: NetworkResource): void => {
        this.mCachedAvailability.set(resource, node.getAvailability());
      }
    );

    // --- Initialize per-resource listener sets ---
    this.mNodes.forEach(
      (_node: NetworkResourceNode, resource: NetworkResource): void => {
        this.mListeners.set(resource, new Set());
      }
    );

    // --- Initialize all nodes ---
    // mIsInitializing suppresses propagation during this creation and wiring so that
    // partially-initialized graph is never evaluated.
    this.mNodes.forEach((node: NetworkResourceNode): void => {
      node.init();
    });

    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "createAndWireNodes() dependency graph ready"
    );
  }

  /**
   * Replays the last buffered hint captured during
   * initialization, or seeds the graph from the NI node if nothing was
   * buffered. Called once immediately after all nodes are initialized.
   */
  private startUp(): void {
    if (this.mPendingHint !== null) {
      // A hint arrived during initialization — replay it now.
      const hint: PendingHint = this.mPendingHint;
      this.mPendingHint = null;
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "startUp() replaying hint buffered during initialization for " +
          String(hint.resource)
      );
      this.dispatchHintToNode(
        hint.resource,
        hint.isAvailable,
        hint.reason
      ).catch((error: unknown): void => {
        Log.e(
          NetworkAvailabilityServiceImpl.TAG,
          "startUp() async dispatch failed",
          error
        );
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Propagation Coordination
  // ──────────────────────────────────────────────────────────────────

  /**
   * Callback invoked by nodes when their state changes.
   * If a propagation cycle is in progress, stores the change as the pending
   * entry, discarding any previously buffered entry. Only the last received
   * change is retained and processed after the cycle settles.
   * Otherwise, starts a new propagation cycle immediately.
   * @param node - The node that changed state
   */
  private onNodeStateChanged(node: NetworkResourceNode): void {
    // While the graph is still being wired, drop state-change events
    if (this.mIsInitializing) {
      return;
    }

    const resource: NetworkResource = node.getResource();

    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "onNodeStateChanged() " +
        resource +
        " → available=" +
        String(node.getAvailability().isAvailable)
    );

    // Remove from pending if it was being re-evaluated
    this.mPendingNodes.delete(resource);

    if (this.mIsPropagating) {
      // State change caused by the current propagation cycle — not an external event.
      if (this.mPropagationVisited.has(resource)) {
        Log.d(
          NetworkAvailabilityServiceImpl.TAG,
          "onNodeStateChanged() cycle detected for " +
            resource +
            " — already visited, skipping"
        );
        return;
      }
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "onNodeStateChanged() queuing propagation-caused change for " + resource
      );
      this.mPropagationQueue.push(node);
      return;
    }

    this.startPropagationCycle(node);
  }

  /**
   * Starts a propagation cycle for the given node's state change.
   * Iterates through the node's dependency map and applies propagation
   * or re-evaluation rules to each dependent node, awaiting async
   * operations (e.g. network probes) before proceeding.
   * @param sourceNode - The node that triggered the propagation
   */
  private async startPropagationCycle(
    sourceNode: NetworkResourceNode
  ): Promise<void> {
    this.mIsPropagating = true;
    this.mPropagationVisited.clear();
    this.mPropagationQueue = [];

    // Snapshot current state for cache (consumers see pre-evaluation values)
    this.snapshotAvailability();

    // Propagate the initial source node
    await this.propagateNode(sourceNode);

    // Drain the propagation queue — nodes that changed state as a consequence
    // of propagation are further propagated within the same cycle.
    while (this.mPropagationQueue.length > 0) {
      const nextNode: NetworkResourceNode | undefined =
        this.mPropagationQueue.shift();
      if (nextNode !== undefined) {
        await this.propagateNode(nextNode);
      }
    }

    // Check if all pending nodes have settled
    await this.checkSettlement();
  }

  /**
   * Propagates a single node's state change to its dependents.
   * Marks the node as visited to prevent cycle re-entry.
   * @param node - The node whose dependencies to propagate
   */
  private async propagateNode(node: NetworkResourceNode): Promise<void> {
    const resource: NetworkResource = node.getResource();

    if (this.mPropagationVisited.has(resource)) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "propagateNode() " + resource + " already visited — skipping"
      );
      return;
    }

    this.mPropagationVisited.add(resource);

    const isAvailable: boolean = node.getAvailability().isAvailable;
    const dependencies: Map<
      NetworkResourceNode,
      { onUnavailable: PropagationRule; onAvailable: PropagationRule }
    > = node.getDependencies();

    for (const [targetNode, config] of dependencies) {
      const rule: PropagationRule = isAvailable
        ? config.onAvailable
        : config.onUnavailable;

      await this.applyRule(rule, node, targetNode);
    }
  }

  /**
   * Applies a propagation rule from the source node to the target node.
   * For RE_EVALUATE rules, awaits the target node's async re-evaluation
   * (e.g. network probes) before returning.
   * @param rule - The propagation rule to apply
   * @param sourceNode - The node whose state changed
   * @param targetNode - The dependent node to propagate to
   */
  private async applyRule(
    rule: PropagationRule,
    sourceNode: NetworkResourceNode,
    targetNode: NetworkResourceNode
  ): Promise<void> {
    const targetResource: NetworkResource = targetNode.getResource();

    if (this.mPropagationVisited.has(targetResource)) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "applyRule() skipping " +
          targetResource +
          " — already visited in this cycle"
      );
      return;
    }

    if (rule === PropagationRule.PROPAGATE) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "applyRule() PROPAGATE " +
          sourceNode.getResource() +
          " → " +
          targetResource
      );

      if (!sourceNode.getAvailability().isAvailable) {
        // Source unavailable → stop target's probing, propagate unavailable
        targetNode.stopInvalidation();
      }

      targetNode.copyAvailabilityFrom(sourceNode);
    } else if (rule === PropagationRule.RE_EVALUATE) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "applyRule() RE_EVALUATE " +
          sourceNode.getResource() +
          " → " +
          targetResource
      );

      // Mark as pending re-evaluation
      this.mPendingNodes.add(targetResource);
      await targetNode.invalidateState();
      // If state did not change, onNodeStateChanged was not called — clear the pending marker.
      this.mPendingNodes.delete(targetResource);
    }
  }

  /**
   * Checks whether all pending nodes have settled.
   * If so, ends the propagation cycle, notifies listeners, and
   * processes any queued changes. Processing queued entries may
   * trigger further async propagation cycles.
   */
  private async checkSettlement(): Promise<void> {
    if (this.mPendingNodes.size > 0) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "checkSettlement() " +
          String(this.mPendingNodes.size) +
          " node(s) still pending"
      );
      return;
    }

    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "checkSettlement() all nodes settled — notifying listeners"
    );

    this.mIsPropagating = false;
    this.mPropagationVisited.clear();
    this.mPropagationQueue = [];

    // Notify per-resource listeners for resources that actually changed
    this.notifyResourceListeners();

    // Notify end-of-cycle evaluation listeners
    this.notifyEvaluationListeners();

    // Update cache
    this.snapshotAvailability();

    // Process the last buffered hint received during the propagation cycle
    if (this.mPendingHint !== null) {
      const hint: PendingHint = this.mPendingHint;
      this.mPendingHint = null;
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "checkSettlement() processing last buffered hint for " +
          String(hint.resource)
      );
      await this.dispatchHintToNode(
        hint.resource,
        hint.isAvailable,
        hint.reason
      );
    }
  }

  /**
   * Notifies per-resource listeners for resources whose availability
   * has changed since the last snapshot.
   */
  private notifyResourceListeners(): void {
    this.mNodes.forEach(
      (node: NetworkResourceNode, resource: NetworkResource): void => {
        const cached: INetworkResourceAvailability | undefined =
          this.mCachedAvailability.get(resource);

        const currentAvailability: INetworkResourceAvailability =
          node.getAvailability();
        const hasChanged: boolean =
          cached === undefined ||
          cached.isAvailable !== currentAvailability.isAvailable ||
          cached.failureReason !== currentAvailability.failureReason;

        if (hasChanged) {
          const listeners: Set<INetworkResourceListener> | undefined =
            this.mListeners.get(resource);
          if (listeners !== undefined) {
            listeners.forEach((listener: INetworkResourceListener): void => {
              try {
                listener.onNetworkResourceAvailabilityChanged(
                  resource,
                  currentAvailability
                );
              } catch (error: unknown) {
                Log.e(
                  NetworkAvailabilityServiceImpl.TAG,
                  "notifyResourceListeners() listener threw for " + resource,
                  error
                );
              }
            });
          }
        }
      }
    );
  }

  /**
   * Notifies all end-of-cycle evaluation listeners.
   */
  private notifyEvaluationListeners(): void {
    this.mEvaluationListeners.forEach(
      (listener: INetworkAvailabilityEvaluationListener): void => {
        try {
          listener.onNetworkAvailabilityEvaluationComplete();
        } catch (error: unknown) {
          Log.e(
            NetworkAvailabilityServiceImpl.TAG,
            "notifyEvaluationListeners() listener threw",
            error
          );
        }
      }
    );
  }

  /**
   * Snapshots current availability for all nodes into the cache.
   * Cache is used to serve getAvailability() during re-evaluation.
   */
  private snapshotAvailability(): void {
    this.mNodes.forEach(
      (node: NetworkResourceNode, resource: NetworkResource): void => {
        this.mCachedAvailability.set(resource, node.getAvailability());
      }
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // External Hint API
  // ──────────────────────────────────────────────────────────────────

  /**
   * @inheritdoc
   */
  public hintNetworkResourceAvailability(
    resource: NetworkResource,
    isAvailable: boolean,
    reason: NetworkResourceFailureReason | null
  ): void {
    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "hintNetworkResourceAvailability() resource=" +
        resource +
        ", available=" +
        String(isAvailable) +
        ", reason=" +
        String(reason)
    );

    if (this.mIsInitializing) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "hintNetworkResourceAvailability() buffering during initialization for " +
          String(resource)
      );
      this.mPendingHint = {
        resource: resource,
        isAvailable: isAvailable,
        reason: reason
      };
      return;
    }

    if (this.mIsPropagating) {
      Log.d(
        NetworkAvailabilityServiceImpl.TAG,
        "hintNetworkResourceAvailability() buffering latest hint (propagation in progress)"
      );
      this.mPendingHint = {
        resource: resource,
        isAvailable: isAvailable,
        reason: reason
      };
      return;
    }

    this.dispatchHintToNode(resource, isAvailable, reason).catch(
      (error: unknown): void => {
        Log.e(
          NetworkAvailabilityServiceImpl.TAG,
          "hintNetworkResourceAvailability() async dispatch failed",
          error
        );
      }
    );
  }

  /**
   * Dispatches a hint directly to the target resource node.
   * Awaits the node's hint handler since it may trigger async
   * operations such as network probes.
   * @param targetResource - The resource whose node should receive the hint
   * @param isAvailable - The suspected new availability (hint)
   * @param reason - The failure reason, if the resource is suspected unavailable
   */
  private async dispatchHintToNode(
    targetResource: NetworkResource,
    isAvailable: boolean,
    reason: NetworkResourceFailureReason | null
  ): Promise<void> {
    const targetNode: NetworkResourceNode | undefined =
      this.mNodes.get(targetResource);

    if (targetNode === undefined) {
      Log.w(
        NetworkAvailabilityServiceImpl.TAG,
        "dispatchHintToNode() no node found for " + String(targetResource)
      );
      return;
    }

    Log.i(
      NetworkAvailabilityServiceImpl.TAG,
      "dispatchHintToNode() dispatching to " + String(targetResource)
    );

    try {
      await targetNode.onNetworkResourceAvailabilityHint(
        targetResource,
        isAvailable,
        reason
      );
    } catch (error: unknown) {
      Log.e(
        NetworkAvailabilityServiceImpl.TAG,
        "dispatchHintToNode() node threw for " + String(targetResource),
        error
      );
    }
  }
}

export { NetworkAvailabilityServiceImpl };
